import { type ReactNode } from "react";
import { ChevronRight, CircleHelp, Sparkles } from "lucide-react";

import { cn } from "@/modern/utils";

import type { NotificationFilter, NotificationSummaryModel } from "../types";
import { NotificationTileButton } from "./NotificationTileButton";

function StatChip({
  label,
  value,
  toneClass,
  onClick,
  ariaLabel,
}: {
  label: string;
  value: number;
  toneClass: string;
  onClick: () => void;
  ariaLabel: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      className={cn(
        "group flex h-9 cursor-pointer items-center gap-2 rounded-full border px-3",
        "transition-[background-color,border-color,color] duration-150",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        "active:opacity-90",
        toneClass,
      )}
    >
      <span className="text-[10px] font-medium uppercase tracking-widest text-muted-foreground transition-colors duration-150 group-hover:text-foreground">
        {label}
      </span>
      <span className="text-[15px] font-medium tabular-nums text-foreground">
        {value}
      </span>
    </button>
  );
}

export function NotificationSummaryView({
  summary,
  onOpenNotable,
  onOpenAll,
  onOpenAsk,
  closeButton,
}: {
  summary: NotificationSummaryModel;
  onOpenNotable: () => void;
  onOpenAll: (filter: NotificationFilter, label?: string) => void;
  onOpenAsk: () => void;
  closeButton?: ReactNode;
}) {
  return (
    <div className="px-5 pb-6 pt-5 sm:px-7">
      <header className="mb-6">
        <div className="mb-4 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-[2.5rem] font-bold leading-[1.1] tracking-tight text-foreground sm:text-[3rem]">
              Updates
            </h1>
          </div>
          {closeButton}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <StatChip
            label="Total"
            value={summary.total}
            toneClass="border-primary/40 bg-primary/10 hover:border-primary/80 hover:bg-primary/25"
            ariaLabel="View all updates"
            onClick={() => onOpenAll({ kind: "all" })}
          />
          <StatChip
            label="Unread"
            value={summary.unread}
            toneClass="border-chart-1/40 bg-chart-1/10 hover:border-chart-1/80 hover:bg-chart-1/25"
            ariaLabel="View unread updates"
            onClick={() => onOpenAll({ kind: "unread" })}
          />
          <StatChip
            label="Notable"
            value={summary.notable}
            toneClass="border-destructive/40 bg-destructive/10 hover:border-destructive/80 hover:bg-destructive/25"
            ariaLabel="View notable updates"
            onClick={onOpenNotable}
          />
        </div>
      </header>

      <div className="mb-5 rounded-[1.5rem] border border-border/60 bg-muted/20 p-4 sm:p-5">
        <SummaryParagraph
          summary={summary}
          onOpenAll={onOpenAll}
        />
      </div>

      <div className="grid grid-cols-4 gap-3 sm:grid-cols-8">
        <NotificationTileButton
          onClick={onOpenNotable}
          span="col-span-4 row-span-2"
          classes={{ bg: "bg-violet-500", text: "text-white", ring: "ring-violet-500/20" }}
          big
          eyebrow={`${summary.notable} worth reviewing`}
          value={`${summary.notable} Notable`}
          caption="Failures, decisions, unusual changes"
          icon={CircleHelp}
        />
        <NotificationTileButton
          onClick={() => onOpenAll({ kind: "all" })}
          span="col-span-4"
          muted
          eyebrow="Complete record"
          value={`View all ${summary.total}`}
          caption="Triggered notifications · system activity · earlier"
          icon={ChevronRight}
        />
        <NotificationTileButton
          onClick={onOpenAsk}
          span="col-span-4"
          classes={{ bg: "bg-teal-500", text: "text-white", ring: "ring-teal-500/20" }}
          eyebrow="Ask Swivu"
          value="About these updates"
          caption="What failed? Show schedule changes."
          icon={Sparkles}
        />
        {summary.statusTiles.map((tile) => (
          <NotificationTileButton
            key={tile.id}
            onClick={() => onOpenAll(tile.filter, tile.caption)}
            span={tile.span}
            classes={tile.classes}
            eyebrow={tile.eyebrow}
            value={tile.value}
            caption={tile.caption}
            icon={tile.icon}
          />
        ))}
      </div>

      {summary.categoryTiles.length > 0 ? (
        <section className="mt-7" aria-labelledby="notification-category-atlas">
          <div className="mb-3">
            <h2
              id="notification-category-atlas"
              className="text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground"
            >
              Categories
            </h2>
          </div>
          <div className="grid grid-cols-4 gap-3 sm:grid-cols-8">
            {summary.categoryTiles.map((tile) => (
              <NotificationTileButton
                key={tile.id}
                onClick={() => onOpenAll(tile.filter, tile.caption)}
                span={tile.span}
                classes={tile.classes}
                eyebrow={tile.eyebrow}
                value={tile.value}
                caption={tile.caption}
                icon={tile.icon}
              />
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}

function SummaryParagraph({
  summary,
  onOpenAll,
}: {
  summary: NotificationSummaryModel;
  onOpenAll: (filter: NotificationFilter, label?: string) => void;
}) {
  const linkClass =
    "underline decoration-dotted underline-offset-4 text-foreground hover:text-primary";

  if (summary.total === 0) {
    return (
      <p className="text-lg leading-relaxed text-foreground/90">
        No updates across Swivu right now. You&apos;re all caught up.
      </p>
    );
  }

  const bodyClauses = summary.clauses.filter((c) => c.id !== "total");

  return (
    <p className="text-lg leading-relaxed text-foreground/90">
      <button
        type="button"
        className={linkClass}
        onClick={() => onOpenAll({ kind: "all" })}
      >
        {summary.clauses[0]?.text ?? `${summary.total} updates`}
      </button>{" "}
      occurred across Swivu
      {bodyClauses.length === 0 ? "." : ". "}
      {bodyClauses.map((clause, index) => {
        const isLast = index === bodyClauses.length - 1;
        const isSucceeded = clause.id === "succeeded";
        return (
          <span key={clause.id}>
            <button
              type="button"
              className={linkClass}
              onClick={() => onOpenAll(clause.filter, clause.text)}
            >
              {clause.text}
            </button>
            {isSucceeded ? " completed successfully" : null}
            {!isLast ? (index === bodyClauses.length - 2 ? ", and " : ", ") : "."}
          </span>
        );
      })}
    </p>
  );
}
